import type { ContentBlock, SourceRef, VisualSummaryItem } from '../contracts/types.ts';

/** 画面では1つの「研究の要点」として束ねる、順序固定の5項目。 */
export const REPORT_OVERVIEW_LABELS = [
  '困りごと',
  '工夫',
  '検証',
  '結果',
  'まだ言えないこと',
] as const;

export type ReportOverviewLabel = typeof REPORT_OVERVIEW_LABELS[number];

/** 詳細本文の読解順。見出しは生成・検査・表示で共有する。 */
export const REPORT_SECTION_HEADINGS = [
  '実社会での意味',
  '仕組みを順に追う',
  '測定値と比較',
  '何を変え、何は変えない？',
  '限界と、まだ言えないこと',
  '小さく試すなら',
] as const;

export type ReportSectionHeading = typeof REPORT_SECTION_HEADINGS[number];

export interface ReportOverviewItem extends VisualSummaryItem {
  label: ReportOverviewLabel;
  blockId: string;
  provenance: ContentBlock['provenance'];
  confidence: ContentBlock['confidence'];
  sources: SourceRef[];
}

const OVERVIEW_LINE = /^［([^］]+)］\s*(\S(?:.*\S)?)$/;
const OUTLINE_ONLY = /^(?:背景|方法|結果|考察|結論|限界|目次|章|節)(?:について)?$/;

/** 保存済み・同期済みデータでも確認できる、原文位置つき出典の構造判定。 */
export function hasResolvedPaperLocator(sources: unknown): boolean {
  return Array.isArray(sources)
    && sources.some((source: unknown) => {
      if (source == null || typeof source !== 'object') return false;
      const candidate = source as Record<string, unknown>;
      return candidate['kind'] === 'paper'
        && typeof candidate['snapshotId'] === 'string'
        && candidate['snapshotId'].trim().length > 0
        && Number.isSafeInteger(candidate['charStart'])
        && Number.isSafeInteger(candidate['charEnd'])
        && (candidate['charStart'] as number) >= 0
        && (candidate['charEnd'] as number) > (candidate['charStart'] as number)
        && typeof candidate['quote'] === 'string'
        && candidate['quote'].trim().length > 0;
    });
}

/** 1つのcalloutを、研究の要点1項目として解釈する。 */
export function parseReportOverviewBlock(block: ContentBlock | undefined): ReportOverviewItem | null {
  if (!block || block.kind !== 'callout' || typeof block.text !== 'string') return null;
  const match = OVERVIEW_LINE.exec(block.text.trim());
  if (!match?.[1] || !match[2] || !REPORT_OVERVIEW_LABELS.includes(match[1] as ReportOverviewLabel)) return null;
  return {
    label: match[1] as ReportOverviewLabel,
    text: match[2].trim(),
    blockId: block.id,
    provenance: block.provenance,
    confidence: block.confidence,
    sources: block.sources,
  };
}

/** 先頭5ブロックが完全な順序で揃う場合だけ、単一overviewの項目として返す。 */
export function reportOverviewItems(blocks: readonly ContentBlock[] | undefined): ReportOverviewItem[] {
  if (!blocks || blocks.length < REPORT_OVERVIEW_LABELS.length) return [];
  const items = blocks.slice(0, REPORT_OVERVIEW_LABELS.length).map(parseReportOverviewBlock);
  return items.every((item, index) => item?.label === REPORT_OVERVIEW_LABELS[index])
    ? items as ReportOverviewItem[]
    : [];
}

/**
 * 画面の中心主張（見出し）は、検証済み本文の要点［結果］から決定論的に導出する。
 * 生成器が別に書いた文や前回保存した文を見出しに使わない。出典の無い文が
 * 論文ページ最上部の「この論文の中心主張」として出るのを防ぐ。要点5項目が揃わない本文では空を返す。
 */
export function centralClaimFromBlocks(blocks: readonly ContentBlock[] | undefined): string {
  return reportOverviewItems(blocks).find((item) => item.label === '結果')?.text ?? '';
}

/** overviewの5項目と、その後の詳細本文を同じ規則で分ける。 */
export function splitReportBlocks(blocks: readonly ContentBlock[] | undefined): {
  overview: ReportOverviewItem[];
  body: ContentBlock[];
} {
  const source = blocks ? [...blocks] : [];
  const overview = reportOverviewItems(source);
  // 1項目が欠けたり順序を誤った場合も、先頭に連続する要点を本文とは数えない。
  // ただし6個目は残し、完全な5項目に続く重複を検出できるようにする。
  let leadingOverviewCount = 0;
  while (
    leadingOverviewCount < REPORT_OVERVIEW_LABELS.length
    && parseReportOverviewBlock(source[leadingOverviewCount]) !== null
  ) {
    leadingOverviewCount += 1;
  }
  return {
    overview,
    body: source.slice(leadingOverviewCount),
  };
}

/** overview 1項目の順序・長さ・情報区分・出典を共通判定する。 */
export function isPublishableReportOverviewItem(
  item: ReportOverviewItem,
  index: number,
  maxItemCharacters?: number,
): boolean {
  return item.label === REPORT_OVERVIEW_LABELS[index]
    && item.text.length > 0
    && (maxItemCharacters === undefined || [...item.text].length <= maxItemCharacters)
    && !OUTLINE_ONLY.test(item.text)
    // 困りごと〜結果は論文が直接述べる要約。境界だけは、本文から確認できない
    // ことを編集部が明示する場合があるため editorial も認める。
    && (index < REPORT_OVERVIEW_LABELS.length - 1
      ? item.provenance === 'paper'
      : item.provenance === 'paper' || item.provenance === 'editorial')
    && hasResolvedPaperLocator(item.sources);
}

/** 原文根拠を持つ5項目だけを公開用overviewとして認める。 */
export function isPublishableReportOverview(
  blocks: readonly ContentBlock[] | undefined,
  maxItemCharacters?: number,
): boolean {
  const items = reportOverviewItems(blocks);
  if (items.length !== REPORT_OVERVIEW_LABELS.length) return false;
  return items.every((item, index) =>
    isPublishableReportOverviewItem(item, index, maxItemCharacters));
}

/** 指定したh2から次のh2までの本文。節内のh3とその本文は含める。 */
export function reportSectionBlocks(
  blocks: readonly ContentBlock[] | undefined,
  heading: ReportSectionHeading,
): ContentBlock[] {
  if (!blocks) return [];
  const start = blocks.findIndex((block) =>
    block.kind === 'heading' && block.headingLevel !== 3 && block.text?.trim() === heading);
  if (start < 0) return [];
  const end = blocks.findIndex((block, index) =>
    index > start && block.kind === 'heading' && block.headingLevel !== 3);
  return blocks.slice(start + 1, end < 0 ? undefined : end);
}
